// Capture UTM params on first page visit, persist for the session,
// and include them on any /api/checkout/session call.
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const REF_PARAM = "ref"; // shorter URL: ?ref=<uuid> → forwarded as referrer_profile_id
const STORAGE_KEY = "haven_utm_v1";

function _readSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _writeSession(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/**
 * Reads ?utm_* params from the current URL and merges them with any previously
 * captured ones in sessionStorage. First-touch wins per key (we don't overwrite
 * a stored utm_source if the user later clicks an internal link without one).
 *
 * Returns the merged attribution payload — safe to call on every page load.
 */
export function captureAndGetUtm() {
  const fromUrl = {};
  try {
    const sp = new URLSearchParams(window.location.search);
    for (const k of UTM_KEYS) {
      const v = sp.get(k);
      if (v) fromUrl[k] = v;
    }
    // Member-site referral: ?ref=<uuid> → maps to referrer_profile_id when checking out.
    const ref = sp.get(REF_PARAM);
    if (ref) fromUrl.referrer_profile_id = ref;
  } catch {}

  const stored = _readSession() || {};

  // Capture first-touch referrer once, if available.
  if (!stored.referrer && typeof document !== "undefined" && document.referrer) {
    try {
      const ref = new URL(document.referrer);
      // Don't store our own host as referrer.
      if (ref.host && ref.host !== window.location.host) {
        stored.referrer = ref.href.slice(0, 500);
      }
    } catch {}
  }

  // First-touch attribution: only fill keys that aren't already stored.
  const merged = { ...stored };
  const captureKeys = [...UTM_KEYS, "referrer_profile_id"];
  for (const k of captureKeys) {
    if (!merged[k] && fromUrl[k]) merged[k] = fromUrl[k];
  }

  if (Object.keys(merged).length > Object.keys(stored).length) {
    _writeSession(merged);
  }

  return merged;
}

export default captureAndGetUtm;
